/**
 * gc-save — writes one GameChanger import (any mix of scorebook PDF, game stats
 * PDF and stats CSV) into a single game, in one pass.
 *
 * This replaces two persistence paths that each saved half a game: the PDF
 * importer wrote play-by-play and arm-health logs but never the box score, and
 * the CSV importer wrote the box score but lived on a different screen. A coach
 * who sent all three files through the PDF screen saw their CSV accepted, then
 * found the game still asking for stats.
 *
 * Every write is idempotent per game: re-importing replaces this game's at-bats,
 * insight cards, pitching-log entry and box score, and never touches other games.
 */

import { collection, addDoc, getDocs, doc, setDoc, writeBatch, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import { deepClean } from '../utils';
import { track } from '../analytics';
import { calculateXOVR, PlayerStats } from '../xovr-engine';
import { PITCH_COUNT_RULESETS, calculateRestDays } from '../pitch-count-rules';
import { isGameLive } from '../game-utils';
import type { ReconciledGame } from '../pdf/reconcileGameImport';
import { localDay } from './gc-sources';
import { fieldTimeRecordFor, fieldTimeSourceOf, gameLengthInnings } from '../gc-innings';

// Strips undefined values so Firestore doesn't reject the write.
const stripUndefined = (obj: Record<string, any>): Record<string, any> =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

/** Aggregates per-game stat objects into one season-level PlayerStats object. The
 *  same weighted-average logic StatsView and App.tsx playerSeasonStats use. */
export function aggregatePlayerStats(series: any[]): PlayerStats {
  let pa = 0, ab = 0, h = 0, bb = 0, so = 0, rbi = 0, r = 0, sb = 0, xbh = 0;
  let sumOps = 0, opsWeight = 0;
  let sumAvg = 0, avgWeight = 0;
  let sumObp = 0, obpWeight = 0;
  let ip = 0, pitches = 0, soAllowed = 0, bbAllowed = 0;
  let sumWhip = 0, whipWeight = 0;
  let sumEra  = 0, eraWeight  = 0;

  series.forEach((s: any) => {
    if ((s.pa || 0) > 0 || (s.ab || 0) > 0) {
      pa += (s.pa || 0); ab += (s.ab || 0); h += (s.h || 0);
      bb += (s.bb || 0); so += (s.so || 0); rbi += (s.rbi || 0);
      r  += (s.r  || 0); sb += (s.sb || 0); xbh += (s.xbh || 0);
      if (s.ops != null) { sumOps += s.ops * (s.pa || 1); opsWeight += (s.pa || 1); }
      if (s.avg != null) { sumAvg += s.avg * (s.ab || 1); avgWeight += (s.ab || 1); }
      if (s.obp != null) { sumObp += s.obp * (s.pa || 1); obpWeight += (s.pa || 1); }
    }
    if ((s.ip || 0) > 0) {
      ip += (s.ip || 0); pitches += (s.pitches || 0);
      soAllowed += (s.soAllowed || 0); bbAllowed += (s.bbAllowed || 0);
      if (s.whip != null) { sumWhip += s.whip * (s.ip || 1); whipWeight += (s.ip || 1); }
      if (s.era  != null) { sumEra  += s.era  * (s.ip || 1); eraWeight  += (s.ip || 1); }
    }
  });

  return {
    pa, ab, h, bb, so, rbi, r, sb, xbh,
    ops:  opsWeight  > 0 ? sumOps  / opsWeight  : null,
    avg:  avgWeight  > 0 ? sumAvg  / avgWeight  : null,
    obp:  obpWeight  > 0 ? sumObp  / obpWeight  : null,
    ip, pitches, soAllowed, bbAllowed,
    whip: whipWeight > 0 ? sumWhip / whipWeight : null,
    era:  eraWeight  > 0 ? sumEra  / eraWeight  : null,
  };
}

/** GameChanger IP notation ("2.1" = two and a third) to outs. */
const ipToOuts = (ip: number | undefined) => {
  if (!ip || ip <= 0) return 0;
  const whole = Math.floor(ip);
  return whole * 3 + Math.round((ip - whole) * 10);
};

export interface CsvLine {
  playerId: string;
  stats: PlayerStats;
  fieldTime: Record<string, number> | null;
}

export interface SaveGcImportInput {
  team: any;
  players: any[];
  target: { kind: 'existing'; game: any } | { kind: 'new'; opponent: string; day: string; isHome: boolean };
  recon: ReconciledGame | null;
  final?: { us: number; them: number };
  csv: { lines: CsvLine[]; fileName: string; via: 'file' | 'paste' } | null;
  /** GameChanger's count per pitcher, already resolved against Dugout's own. */
  pitchCounts: Record<string, number>;
  files: { scorebook?: string; boxScore?: string };
  /** Called as soon as a new game exists, so a retry after a later failure
   *  saves into it instead of adding the game a second time. */
  onGameCreated?: (game: any) => void;
}

export interface SaveGcImportResult {
  gameId: string;
  createdGame: boolean;
  plateAppearances: number;
  boxScorePlayers: number;
  fieldTimePlayers: number;
  pitchers: number;
  restDaysUpdated: boolean;
  finalScore?: { us: number; them: number; outcome: 'W' | 'L' | 'T'; changedFrom?: string };
  statusLeftLive: boolean;
}

export async function saveGcImport(input: SaveGcImportInput): Promise<SaveGcImportResult> {
  const { team, players, recon, csv, final } = input;
  const teamId = team.id;
  const now = new Date().toISOString();
  const existing = input.target.kind === 'existing' ? input.target.game : null;

  // ── 1. The game: bond to the existing one, or add it to the schedule ─────────
  let gameId = existing?.id as string | undefined;
  let createdGame = false;
  const day = input.target.kind === 'new' ? input.target.day : localDay(existing?.date);
  if (input.target.kind === 'new') {
    const newGame = {
      opponent: input.target.opponent,
      date: `${input.target.day}T12:00`,
      isHome: input.target.isHome,
      status: 'completed',
      importedFrom: recon ? 'scorebook' : 'gamechanger',
      importedAt: now,
      createdAt: now,
    };
    const ref = await addDoc(collection(db, `teams/${teamId}/games`), deepClean(newGame));
    gameId = ref.id;
    createdGame = true;
    input.onGameCreated?.({ ...newGame, id: ref.id });
  }
  const gameDate = existing?.date ? new Date(existing.date) : new Date(`${day}T12:00`);
  const live = !!existing && isGameLive(existing);

  const gamePatch: Record<string, any> = { updatedAt: now };
  const playerPatches: Record<string, Record<string, any>> = {};
  const patchPlayer = (id: string, patch: Record<string, any>) => {
    playerPatches[id] = { ...playerPatches[id], ...patch };
  };

  // A played game is a completed game. A game the live engine is still running
  // is left alone: its own End Game writes the status and score.
  let finalScore: SaveGcImportResult['finalScore'];
  if (!live) {
    gamePatch.status = 'completed';
    if (final) {
      const outcome = final.us > final.them ? 'W' : final.us < final.them ? 'L' : 'T';
      const prev = existing?.result;
      const changed = prev && (prev.myScore !== final.us || prev.opponentScore !== final.them);
      gamePatch.result = { myScore: final.us, opponentScore: final.them, outcome };
      finalScore = { us: final.us, them: final.them, outcome, ...(changed ? { changedFrom: `${prev.myScore}–${prev.opponentScore}` } : {}) };
    }
  }

  // ── 2. Box score, field time, bench time and xOVR from the CSV ───────────────
  let fieldTimePlayers = 0;
  if (csv && csv.lines.length) {
    const gameStats: Record<string, any> = {};
    const gcFieldTime: Record<string, Record<string, number>> = {};
    let pitcherOuts = 0;
    let longestField = 0;
    for (const line of csv.lines) {
      gameStats[line.playerId] = stripUndefined(line.stats as any);
      pitcherOuts += ipToOuts(line.stats.ip ?? 0);
      if (line.fieldTime) {
        gcFieldTime[line.playerId] = line.fieldTime;
        longestField = Math.max(longestField, Object.values(line.fieldTime).reduce((s, n) => s + n, 0));
      }
    }

    // GameChanger's field time is always stored. By default it is also the
    // system of record for a finished game and supersedes whatever the live
    // engine logged (a one-inning live stub used to block it). A team on "Dugout
    // live games" keeps a whole-game live log as the record instead, so the
    // bench counts that game already wrote at End Game are left alone (support
    // log G9).
    fieldTimePlayers = Object.keys(gcFieldTime).length;
    let gameInnings = existing?.innings || Math.max(Math.ceil(pitcherOuts / 3), Math.ceil(longestField));
    if (!gameInnings) gameInnings = 6;
    // The game as every screen will measure it once this import is saved, so
    // these bench counts match the Stats tab and the Settings rebuild.
    const afterImport = { ...existing, gcFieldTime, inferredInnings: gameInnings };
    const gameLength = gameLengthInnings(afterImport, team?.defaultInnings);
    const dugoutKeepsBench = !!existing &&
      fieldTimeRecordFor(afterImport, fieldTimeSourceOf(team), team?.defaultInnings) === 'live';
    if (fieldTimePlayers) {
      gamePatch.gcFieldTime = gcFieldTime;
      gamePatch.inferredInnings = gameInnings;
      if (!dugoutKeepsBench) {
        for (const line of csv.lines) {
          if (!line.fieldTime) continue;
          const player = players.find(p => p.id === line.playerId);
          if (!player) continue;
          const inField = Object.values(line.fieldTime).reduce((s, n) => s + n, 0);
          const priorBenchLog = player.benchLog && typeof player.benchLog === 'object' ? player.benchLog : {};
          const benchLog = { ...priorBenchLog, [gameId!]: Math.max(0, gameLength - inField) };
          patchPlayer(line.playerId, {
            benchLog,
            seasonBenchInnings: Object.values(benchLog).reduce((s: number, n) => s + (n as number), 0),
          });
        }
      }
    }

    // xOVR from the full season sample (one game alone barely moves confidence).
    // The formula itself is untouched; this is the same call StatsImportModal made.
    const allGames = await getDocs(collection(db, `teams/${teamId}/games`));
    const series: Record<string, any[]> = {};
    allGames.docs.forEach(g => {
      if (g.id === gameId) return;  // this game's stats are the new ones below
      const s = g.data().stats;
      if (!s) return;
      Object.entries(s).forEach(([pid, line]) => { (series[pid] ||= []).push(line); });
    });
    for (const line of csv.lines) (series[line.playerId] ||= []).push(gameStats[line.playerId]);

    for (const line of csv.lines) {
      const player = players.find(p => p.id === line.playerId);
      if (!player) continue;
      const seasonStats = aggregatePlayerStats(series[line.playerId]);
      const scoutingOVR = player?.ovrBreakdown?.scoutingOVR ?? player?.ovr ?? 0;
      const breakdown = calculateXOVR(scoutingOVR, seasonStats);
      patchPlayer(line.playerId, {
        stats: stripUndefined(seasonStats as any),
        ovr: breakdown.xovr,
        ovrBreakdown: breakdown,
      });
      // Freeze xOVR at this game so the trendline stays historically accurate.
      gameStats[line.playerId] = { ...gameStats[line.playerId], xovrSnapshot: breakdown.xovr };
    }
    gamePatch.stats = gameStats;
  }

  // ── 3. Pitch counts → the game, and each pitcher's arm-health log ────────────
  const pitchEntries = Object.entries(input.pitchCounts).filter(([, n]) => n > 0);
  let restDaysUpdated = false;
  if (pitchEntries.length) {
    gamePatch.pitchCounts = { ...(existing?.pitchCounts || {}), ...Object.fromEntries(pitchEntries) };

    // Coach pitch and machine pitch have no pitch-count rules to apply.
    const style = team.settings?.styleOfPlay ?? team.styleOfPlay;
    if (style !== 'tballcoachpitch' && style !== 'machinepitch') {
      const ruleset = PITCH_COUNT_RULESETS[team.settings?.pitchCountRuleset || 'little_league'] || PITCH_COUNT_RULESETS['little_league'];
      const ageGroup = team.settings?.pitchCountAgeGroup || team.settings?.ageGroup || team.ageGroup || '12U';
      const ageRules = (ruleset.id === 'custom' && team.settings?.customRules)
        ? team.settings.customRules
        : (ruleset.ageGroups[ageGroup] || ruleset.ageGroups['all'] || ruleset.ageGroups['12U'] || { dailyMax: 85, restThresholds: [] });
      for (const [pitcherId, count] of pitchEntries) {
        const player = players.find(p => p.id === pitcherId);
        if (!player) continue;
        const restDays = calculateRestDays(count, ageRules.restThresholds || []);
        const eligible = new Date(gameDate);
        eligible.setDate(eligible.getDate() + restDays + 1);
        eligible.setHours(0, 0, 0, 0);
        const others = (player.pitchingLog || []).filter((e: any) => e.gameId !== gameId);
        patchPlayer(pitcherId, {
          pitchingLog: [...others, deepClean({
            gameId,
            date: gameDate.toISOString(),
            pitchCount: count,
            requiredRestDays: restDays,
            eligibleDate: eligible.toISOString(),
            tournamentId: existing?.tournamentId || null,
            opponent: existing?.opponent || recon?.opponent || 'Unknown',
          })],
        });
        restDaysUpdated = true;
      }
    }
  }

  // ── 4. Play-by-play (replace this game's at-bats) ────────────────────────────
  if (recon && recon.atBats.length) {
    await replaceSubcollection(teamId, gameId!, 'atBats', recon.atBats.map(ab => ({ ...ab, gameId })));
  }

  // ── 5. Provenance: what this game has, and from which files ──────────────────
  const gcImport: Record<string, any> = { updatedAt: now };
  if (recon) gcImport.scorebook = { at: now, fileName: input.files.scorebook || '', plateAppearances: recon.atBats.length };
  if (input.files.boxScore) gcImport.boxScore = { at: now, fileName: input.files.boxScore };
  if (csv) gcImport.csv = { at: now, fileName: csv.fileName, via: csv.via, players: csv.lines.length };
  gamePatch.gcImport = gcImport;

  await setDoc(doc(db, `teams/${teamId}/games`, gameId!), deepClean(gamePatch), { merge: true });
  for (const [pid, patch] of Object.entries(playerPatches)) {
    await setDoc(doc(db, `teams/${teamId}/players`, pid), patch, { merge: true });
  }

  // ── 6. Opponent scouting record, accumulated across meetings ─────────────────
  if (recon?.opponentRecord && recon.opponentKey) {
    // arrayUnion must stay OUTSIDE deepClean: deepClean copies objects into plain
    // ones, which turned the sentinel into a stored map ({_methodName, _elements})
    // in the old importer. arrayUnion over that map replaces it with a real array.
    const { gameIds: _ignored, ...record } = recon.opponentRecord;
    await setDoc(doc(db, `teams/${teamId}/opponents`, recon.opponentKey), {
      ...deepClean({ ...record, lastSeen: recon.date || day || record.lastSeen }),
      gameIds: arrayUnion(gameId),
    }, { merge: true });
  }

  // ── 7. Import-success marker + analytics (only when stats really landed) ─────
  // The marker's only reader is syncCoachOnTeamUpdate, which flips Loops'
  // `statsImported` and fires `stats_imported`. Lifecycle emails branch on that,
  // so a PDF-only share counts too: otherwise a coach who imported is told they
  // haven't. Analytics stay CSV-only so the stats_imported trend keeps its meaning.
  const csvLanded = !!(csv && csv.lines.length);
  const pdfLanded = !!(recon && recon.atBats.length) || !!input.files.boxScore;
  if (csvLanded || pdfLanded) {
    await setDoc(doc(db, 'teams', teamId), {
      lastStatsImport: csvLanded
        ? { at: now, mode: 'currentSeason', playersMatched: csv!.lines.length, isSeasonLevel: false, gameId }
        : { at: now, mode: 'gamePdf', playersMatched: 0, isSeasonLevel: false, gameId },
    }, { merge: true });
  }
  if (csvLanded) {
    track('stats_imported', { mode: 'currentSeason', playersMatched: csv!.lines.length, isSeasonLevel: false });
  }

  return {
    gameId: gameId!,
    createdGame,
    plateAppearances: recon?.atBats.filter(a => a.batterTeam === 'us').length ?? 0,
    boxScorePlayers: csv?.lines.length ?? 0,
    fieldTimePlayers,
    pitchers: pitchEntries.length,
    restDaysUpdated,
    finalScore,
    statusLeftLive: live,
  };
}

/** Replace this game's AI insight cards. Separate from the save so a slow or
 *  failed insight call never costs the coach their imported game. */
export async function saveGameInsights(teamId: string, gameId: string, cards: any[]): Promise<void> {
  const at = new Date().toISOString();
  await replaceSubcollection(teamId, gameId, 'insights', cards.map(c => ({
    ...c, gameId, generatedAt: at, dismissed: false, tier: 'game', source: 'ai',
  })));
  await setDoc(doc(db, `teams/${teamId}/games`, gameId), { gcImport: { insights: { at, count: cards.length } } }, { merge: true });
}

/** The insight cards already saved on a game. A read, never a Claude call. */
export async function loadGameInsights(teamId: string, gameId: string): Promise<any[]> {
  const snap = await getDocs(collection(db, `teams/${teamId}/games/${gameId}/insights`));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

async function replaceSubcollection(teamId: string, gameId: string, sub: string, docs: any[]) {
  const path = `teams/${teamId}/games/${gameId}/${sub}`;
  const snap = await getDocs(collection(db, path));
  // Firestore caps a batch at 500 writes; a game is ~50 plate appearances, but
  // chunk anyway so an extra-innings tournament game can't fail the import.
  const ops: Array<(b: ReturnType<typeof writeBatch>) => void> = [
    ...snap.docs.map(d => (b: ReturnType<typeof writeBatch>) => b.delete(d.ref)),
    ...docs.map(data => (b: ReturnType<typeof writeBatch>) => b.set(doc(collection(db, path)), deepClean(data))),
  ];
  for (let i = 0; i < ops.length; i += 450) {
    const batch = writeBatch(db);
    ops.slice(i, i + 450).forEach(op => op(batch));
    await batch.commit();
  }
}
